import React from "react";
import { Box, Skeleton, Tooltip } from "@mui/material";
import { tokens, mono } from "../theme";
// LEGACY — delete this import and the isLegacy branches to remove it.
import { LEGACY_DASH, LEGACY_DATE_OPTIONS } from "../components/commonComponents/appShell/legacy/legacyMissing";

/**
 * Replaces the single `dash` export in tableUtils.tsx.
 *
 * Six states, because "missing" means six different things and they differ by
 * what the user should do next. Two rules that are easy to get wrong:
 *   - Zero is a value. If the query returned 0, render 0. A dash means we do not know.
 *   - Grey means nothing is there; amber means something went wrong. Never swap them.
 */
export type MissingKind = "absent" | "na" | "pending" | "empty" | "filtered" | "error";

// U+2013 EN DASH, not U+30FC (the katakana mark the old `dash` used).
const GLYPH = "\u2013";
// LEGACY — the old table placeholder. Delete with the rest of the legacy shell.
const isLegacy = tokens.shell === "legacy";

const glyphSx = {
  fontFamily: mono,
  color: tokens.color.faint, // #93A2B0 — 3.4:1, clears AA at this size
  fontVariantNumeric: "tabular-nums" as const
};

/** Field is genuinely blank on an existing record. */
export const absent = isLegacy ? (
  LEGACY_DASH
) : (
  <Box component="span" sx={glyphSx}>{GLYPH}</Box>
);

/** Column cannot apply to this row. Same glyph, but says why on hover. */
export const notApplicable = (reason = "Not applicable to this row") => (
  <Tooltip title={reason} arrow>
    <Box component="span" sx={{ ...glyphSx, cursor: "help" }}>{GLYPH}</Box>
  </Tooltip>
);

/** Shaped like the value it will become, so nothing shifts when data lands. */
export const pending = (width: number | string = 64) => (
  <Skeleton variant="rounded" width={width} height={14} sx={{ borderRadius: 1 }} />
);

/** The one state that must never be mistaken for a real zero. */
export const failed = (onRetry?: () => void) => (
  <Tooltip title={onRetry ? "Failed to load — click to retry" : "Failed to load"} arrow>
    <Box
      component="span"
      onClick={onRetry}
      sx={{
        fontFamily: mono,
        fontWeight: 600,
        color: tokens.color.caution.text, // amber, never grey
        cursor: onRetry ? "pointer" : "help"
      }}
    >
      !
    </Box>
  </Tooltip>
);

export const missing = { absent, notApplicable, pending, failed };

interface CellValueProps {
  value: unknown;
  kind?: MissingKind;
  onRetry?: () => void;
  /** Sentinel the API uses for "no value" — this codebase uses -1 for grades. */
  nullish?: unknown[];
  render?: (v: unknown) => React.ReactNode;
}

/**
 * Drop-in for any material-react-table Cell renderer.
 *   Cell: ({ cell }) => <CellValue value={cell.getValue()} nullish={[-1, ""]} />
 */
export const CellValue = ({ value, kind, onRetry, nullish = ["", null, undefined], render }: CellValueProps) => {
  if (kind === "pending") return pending();
  if (kind === "error") return failed(onRetry);
  if (kind === "na") return notApplicable();
  if (nullish.some((n) => Object.is(n, value))) return absent;
  return <>{render ? render(value) : String(value)}</>;
};

/** formatDate, ported from tableUtils.tsx onto the new glyph. */
const dateFormatOptions: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric"
};

export const formatDate = (value?: Date | string | null): React.ReactNode =>
  value
    ? new Intl.DateTimeFormat(
        navigator.language,
        // The original showed the weekday and the full time; the refresh
        // shortened it. Legacy keeps the long form.
        isLegacy ? LEGACY_DATE_OPTIONS : dateFormatOptions
      ).format(new Date(value))
    : absent;

/**
 * Date with no time of day, whatever the shell. For columns where the clock
 * time is noise — a grade was assigned on a day, not at 14:32:07. Grade items
 * keep formatDate, because there the time is the point.
 */
export const formatDateOnly = (value?: Date | string | null): React.ReactNode =>
  value
    ? new Intl.DateTimeFormat(navigator.language, dateFormatOptions).format(new Date(value))
    : absent;

export default missing;
